import { Mic } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useAssistantContext } from "@/contexts/AssistantContext";
import { useCanUseAssistant } from "@/hooks/useCanUseAssistant";
import { VoiceTaskPanel } from "./VoiceTaskPanel";
import { AssistantChatPanel } from "./AssistantChatPanel";
import { IncidentReportWizard } from "@/components/worker/IncidentReportWizard";
import { FocusSessionSheet } from "@/components/health-map/FocusSessionSheet";
import { useAuth } from "@/contexts/AuthContext";
import { ReportModeChoices, type ReportMode } from "@/components/pests/ReportModePicker";
import { VoiceModalShell } from "@/components/voice/VoiceModalShell";
import { useOnboardingGate } from "@/components/routing/OnboardingGate";
import { useAssistantIsPage } from "@/hooks/useAssistantIsPage";
import { rememberAppRoute } from "./assistantSuggestions";

export function AssistantButton() {
  const { isPanelOpen, setIsPanelOpen, togglePanel, activePanel, setActivePanel } = useAssistantContext();
  // En escritorio el chat vive en su subpágina (/advanced), así que aquí no se
  // abre nunca: la pestaña ya no está en el conmutador y un `activePanel` viejo
  // —o heredado de un ancho de pantalla más estrecho— caería en un panel sin
  // pestaña con la que volver. Por debajo de `lg` no cambia nada.
  const assistantIsPage = useAssistantIsPage();
  const canChat = useCanUseAssistant();
  const assistantAllowed = canChat && !assistantIsPage;
  const { t } = useTranslation();
  // While first-admin onboarding is in progress the voice toggle must not appear
  // at all — on any device. The mobile dock's center mic is already hidden by the
  // same gate (see MobileDock); this hides the desktop floating trigger too so the
  // assistant only shows up once setup is fully completed.
  const { active: onboardingActive } = useOnboardingGate();
  const { company } = useAuth();
  /** El parte recién enviado: se abre su ficha, con lo que dice la IA (28-09-2026). */
  const [justSentId, setJustSentId] = useState<string | null>(null);
  // Muestreo: selector primero, asistente después.
  const [reportWizardOpen, setReportWizardOpen] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>("voice");
  /** Fotos hechas desde el propio selector (tocar «Foto» abre la cámara al momento). */
  const [reportInitialPhotos, setReportInitialPhotos] = useState<File[] | undefined>(undefined);
  // Al cerrar el panel —por donde sea— se vuelve al selector, o al reabrir Muestreo se
  // entraría directo al asistente con la elección anterior ya hecha.
  useEffect(() => {
    if (!isPanelOpen || activePanel !== "scoutingVoice") setReportWizardOpen(false);
  }, [isPanelOpen, activePanel]);
  const { pathname } = useLocation();
  /* Está montado en todas las pantallas (MainLayout): apunta en cuál estás para
     que el chat de escritorio (/advanced) sugiera lo de esa pantalla. */
  useEffect(() => {
    rememberAppRoute(pathname);
  }, [pathname]);
  const onAssistant = pathname.startsWith("/advanced");
  if (onAssistant) return null;
  // Hide the whole assistant (trigger + panels) until onboarding is complete.
  if (onboardingActive) return null;


  return (
    <>
      <AnimatePresence>
        {!isPanelOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={togglePanel}
            data-assistant-fab
            className={cn(
              /* `data-assistant-fab`: con un mapa ocupando la pantalla entera este
                 botón se queda flotando ENCIMA del plano, justo sobre la esquina
                 donde vive la leyenda. Se esconde por CSS con `body.map-immersive`
                 (ver index.css), igual que la barra de abajo del teléfono. */
              "fixed bottom-6 right-6 z-50 hidden lg:flex",
              "w-14 h-14 rounded-full",
              "ipm-gradient-main",
              "items-center justify-center",
              "shadow-lg shadow-primary/25",
              "transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
            )}
            aria-label="Open voice modal"
          >
            <Mic className="w-6 h-6 text-white" />
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {/* Cinturón y tirantes: si un rol sin acceso al chat acaba con
            activePanel="aiChat" (p. ej. estado heredado), se le enseña el panel
            de voz en su lugar — la pestaña ya va oculta en VoiceModalShell. */}
        {isPanelOpen && (activePanel === "taskVoice" || (activePanel === "aiChat" && !assistantAllowed)) && <VoiceTaskPanel />}
      </AnimatePresence>
      <AnimatePresence>
        {isPanelOpen && activePanel === "aiChat" && assistantAllowed && <AssistantChatPanel />}
      </AnimatePresence>
      {/* Muestreo: primero se elige foto o voz, igual que en la página de Plagas. Antes
          entraba derecho a grabar, así que desde aquí no se podía reportar solo con una
          foto.

          Va DENTRO de VoiceModalShell, no en su propia hoja: es la tercera pestaña del
          conmutador, así que sale con el mismo selector Asistente / Tarea por voz /
          Muestreo, la misma cabecera, el mismo alto y el mismo sitio en cada pantalla
          que las otras dos. El selector sustituye a la flecha de atrás que llevaba
          antes — para volver a Tarea por voz se toca su pestaña. */}
      <AnimatePresence>
        {isPanelOpen && activePanel === "scoutingVoice" && !reportWizardOpen && (
          <VoiceModalShell tab="scouting" onClose={() => setIsPanelOpen(false)} bodyClassName="flex flex-col [&>*]:my-0">
            <ReportModeChoices
              onPick={(mode) => {
                setReportMode(mode);
                setReportInitialPhotos(undefined);
                setReportWizardOpen(true);
              }}
              // «Foto» abre la cámara en el mismo toque; el asistente arranca con lo capturado.
              onPickPhotoFiles={(files) => {
                setReportMode("photo");
                setReportInitialPhotos(files);
                setReportWizardOpen(true);
              }}
            />
          </VoiceModalShell>
        )}
      </AnimatePresence>

      {/* Muestreo = audio-first incident report (record audio → photos → location → classify). */}
      <IncidentReportWizard
        open={isPanelOpen && activePanel === "scoutingVoice" && reportWizardOpen}
        onOpenChange={(next) => {
          setReportWizardOpen(next);
          if (!next) {
            setReportInitialPhotos(undefined);
            setIsPanelOpen(false);
          }
        }}
        initialPhotos={reportInitialPhotos}
        initialMode={reportMode}
        anchor="corner"
        allowClassification
        // Si se ha elegido «Voz» a propósito, se empieza a grabar sin un toque más.
        autoStartVoice={reportMode === "voice"}
        // El «Atrás» del primer paso vuelve al selector, no a Tarea por voz: para eso
        // está la flecha de la cabecera del selector.
        onRequestTabSelector={() => setReportWizardOpen(false)}
        onSubmitted={(sessionId) => {
          /* Directo a la ficha del parte, aquí mismo (Pablo, 28-09-2026: «que ya te lleve a
             la sesión y te diga lo que es la IA; mientras te lleva, que la IA analice»).
             Antes salía «¿Qué quieres hacer ahora?»; «Crear tratamiento» está en la ficha.
             Con retardo: abrir una hoja mientras la otra se cierra deja la nueva muerta. */
          setReportWizardOpen(false);
          setIsPanelOpen(false);
          window.setTimeout(() => setJustSentId(sessionId), 200);
        }}
      />

      <FocusSessionSheet sessionId={justSentId} companyId={company?.id} onClose={() => setJustSentId(null)} />

    </>
  );
}
